// "Describe your design" (M5): the two JSON endpoints behind the Describe
// sheet. They are called with a raw fetch(), never Inertia's router, so they
// answer JSON (the CLAUDE.md exception to the redirect rule):
//
//   POST /artwork       { prompt, style, bottle_type } → 202 { id, status }
//                       422 { error: { code: hourly | daily | site_cap | invalid, message } }
//                       429 { error: { code: rate_limited, message } }
//                       404 (empty) when the studio cannot paint (production without a key)
//   GET  /artwork/:id   200 { id, status, options, error } — polled every 2 s
//                       404 (empty) for a run from another session or account
//
// CSRF: the POST sends X-CSRF-Token from the layout's csrf-token meta (the
// Inertia-set XSRF-TOKEN cookie is the fallback; Rails accepts either).
//
// SSR-safe: document and fetch are only touched inside the functions, which
// run from event handlers.

import type { ArtworkStyleKey, BottleType } from "@/types/inertia"

export type ArtworkRunStatus = "queued" | "running" | "done" | "failed"

export type ArtworkOption = { index: number; url: string; width: number; height: number }

export type ArtworkError = { code: string; message: string }

export type ArtworkStatus = {
  id: number
  status: ArtworkRunStatus
  options: ArtworkOption[]
  error: ArtworkError | null
}

export type ArtworkRequest = { prompt: string; style: ArtworkStyleKey; bottle_type: BottleType }

/** Poll cadence and the client's own give-up point (decision 9). */
export const POLL_INTERVAL_MS = 2000
export const POLL_TIMEOUT_MS = 3 * 60 * 1000
/** Consecutive failed polls (network, 5xx) tolerated before giving up. */
const MAX_POLL_FAILURES = 5

/** The sentence for timeouts, outages and anything unexpected (same as the server's). */
export const PAINT_FAILED = "Something went wrong painting your artwork. Please try again in a moment."

/** A run could not be started or followed; `code` picks the sheet's next step. */
export class ArtworkRequestError extends Error {
  code: string
  constructor(code: string, message: string) {
    super(message)
    this.name = "ArtworkRequestError"
    this.code = code
  }
}

function csrfToken(): string | null {
  if (typeof document === "undefined") return null
  const meta = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content
  if (meta) return meta
  const cookie = document.cookie.split("; ").find((c) => c.startsWith("XSRF-TOKEN="))
  return cookie ? decodeURIComponent(cookie.slice("XSRF-TOKEN=".length)) : null
}

function isAbort(error: unknown) {
  return error instanceof DOMException && error.name === "AbortError"
}

/** `{ error: { code, message } }` from a JSON error body, if that is what came back. */
async function errorFrom(response: Response): Promise<ArtworkError | null> {
  try {
    const body = (await response.json()) as { error?: Partial<ArtworkError> }
    const code = body?.error?.code
    const message = body?.error?.message
    return typeof code === "string" && typeof message === "string" ? { code, message } : null
  } catch {
    return null
  }
}

/**
 * Start a run. Resolves with the new run's id; rejects with an
 * ArtworkRequestError carrying the server's code and sentence (caps,
 * "invalid", "rate_limited"), or PAINT_FAILED for anything else. An abort
 * rejects with the AbortError itself.
 */
export async function createArtwork(request: ArtworkRequest, signal?: AbortSignal): Promise<{ id: number }> {
  const headers: Record<string, string> = { Accept: "application/json", "Content-Type": "application/json" }
  const token = csrfToken()
  if (token) headers["X-CSRF-Token"] = token

  let response: Response
  try {
    response = await fetch("/artwork", {
      method: "POST",
      headers,
      credentials: "same-origin",
      body: JSON.stringify(request),
      signal,
    })
  } catch (error) {
    if (isAbort(error)) throw error
    throw new ArtworkRequestError("network", PAINT_FAILED)
  }

  if (response.status === 202) {
    const body = (await response.json().catch(() => null)) as { id?: unknown } | null
    if (typeof body?.id === "number") return { id: body.id }
    throw new ArtworkRequestError("upstream", PAINT_FAILED)
  }
  if (response.status === 422 || response.status === 429) {
    const error = await errorFrom(response)
    if (error) throw new ArtworkRequestError(error.code, error.message)
  }
  // 404 (painting disabled), a stale CSRF token, a 5xx: nothing the visitor can fix by rewording.
  throw new ArtworkRequestError(response.status === 404 ? "unavailable" : "upstream", PAINT_FAILED)
}

/** Resolves after `ms`, or rejects straight away with an AbortError when aborted. */
function wait(ms: number, signal?: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    if (signal?.aborted) return reject(new DOMException("Aborted", "AbortError"))
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort)
      resolve()
    }, ms)
    const onAbort = () => {
      clearTimeout(timer)
      reject(new DOMException("Aborted", "AbortError"))
    }
    signal?.addEventListener("abort", onAbort, { once: true })
  })
}

/**
 * Poll a run every `intervalMs` until it is done or failed, and resolve with
 * that final status (a failed run carries its error). Rejects with an
 * ArtworkRequestError "timeout" after `timeoutMs`, "unavailable" on a 404,
 * "upstream" after several failed polls in a row; an abort rejects with the
 * AbortError. A single dropped poll is simply retried on the next tick.
 */
export async function pollArtwork(
  id: number,
  { signal, intervalMs = POLL_INTERVAL_MS, timeoutMs = POLL_TIMEOUT_MS }: {
    signal?: AbortSignal
    intervalMs?: number
    timeoutMs?: number
  } = {},
): Promise<ArtworkStatus> {
  const deadline = Date.now() + timeoutMs
  let failures = 0
  for (;;) {
    try {
      const response = await fetch(`/artwork/${id}`, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
        signal,
      })
      if (response.status === 404) throw new ArtworkRequestError("unavailable", PAINT_FAILED)
      if (!response.ok) throw new Error(`poll answered ${response.status}`)
      const status = (await response.json()) as ArtworkStatus
      failures = 0
      if (status.status === "done" || status.status === "failed") return status
    } catch (error) {
      if (isAbort(error) || error instanceof ArtworkRequestError) throw error
      failures += 1
      if (failures >= MAX_POLL_FAILURES) throw new ArtworkRequestError("upstream", PAINT_FAILED)
    }
    if (Date.now() + intervalMs > deadline) throw new ArtworkRequestError("timeout", PAINT_FAILED)
    await wait(intervalMs, signal)
  }
}
